import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  type ConstantWithColors,
  LanguageUtils,
  Utils,
} from '@pbt/pbt-ui-components'

import {
  getDiagnosisProblemStates,
  getDiagnosisStatus,
  getFindingProblemStates,
} from '~/store/reducers/constants'
import {
  type DiagnosisSnapshotItem,
  DiagnosisSnapshotItemType,
  type MasterProblemsSnapshotItem,
} from '~/types'

interface UseDiagnosisStatusProps {
  item: MasterProblemsSnapshotItem | DiagnosisSnapshotItem
  shorten?: boolean
}

interface UseDiagnosisStatusReturn {
  activeStatus: ConstantWithColors | undefined
  statusDisplayName: string
}

const useDiagnosisStatus = ({
  item,
  shorten = false,
}: UseDiagnosisStatusProps): UseDiagnosisStatusReturn => {
  const { t } = useTranslation(['Common', 'Clients'])
  const DiagnosisStatus = useSelector(getDiagnosisStatus)
  const ProblemDiagnosisStates = useSelector(getDiagnosisProblemStates)
  const ProblemFindingStates = useSelector(getFindingProblemStates)

  const typeToStatesMap = {
    [DiagnosisSnapshotItemType.DIAGNOSIS_HISTORY]: DiagnosisStatus,
    [DiagnosisSnapshotItemType.DIAGNOSIS_LOG]: DiagnosisStatus,
    [DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG]: ProblemDiagnosisStates,
    [DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG]: ProblemFindingStates,
  }

  const diagnosisStates =
    typeToStatesMap[item.type || DiagnosisSnapshotItemType.DIAGNOSIS_LOG]

  const stateName = LanguageUtils.getConstantTranslatedName(
    item.statusId,
    diagnosisStates,
  )

  const activeStatus =
    item.statusId && Utils.findById(item.statusId, diagnosisStates)

  const isHistoryRecord = !item.soapId

  const statusDisplayName = isHistoryRecord
    ? shorten
      ? t('Common:PREVIOUS_CLINIC')
      : t('Clients:TIMELINE.DIAGNOSIS_CARD.RECORD_FROM_PLACE', {
          diagnosedIn: item.diagnosedIn || t('Common:PREVIOUS_CLINIC'),
        })
    : stateName

  return {
    activeStatus,
    statusDisplayName,
  }
}

export default useDiagnosisStatus
